import { Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { PatientModel } from '../../../core/api/models/patient-model';
import { FormControl, Validators } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { PatientTargetModel } from '../../../core/api/models/patient-target-model';
import { SupervisorApiService } from '../../../core/api/services/supervisor-api.service';
import { AlertService } from '../../../shared/services/alert.service';
import { SupervisorHelperService } from '../../../supervisor/supervisor.helper.service';
import { AuthService } from '../../../auth/auth.service';
import { targetIsEchoic, targetIsPromptBased } from '../../../shared/utils/utils';
import { PatientTargetDataModel } from '../../../core/api/models/patient-target-data-model';
import { PromptCodeModel } from '../../../core/api/models/prompt-code-model';
import { SelectItem } from 'primeng/api';
import { Utils } from '../../../core/utils';
import { UserModel } from '../../../core/api/models/user-model';
import { StaffApiService } from '../../../core/api/services/staff-api.service';
import { GraphApiService } from '../../../core/api/services/graph-api.service';
import { PatientSessionModel } from '../../../core/api/models/patient-session-model';
import { PatientTargetDataToileting, SessionSimpleDataModel, TargetType, DataTakerStaffDataModel } from '../../../core/api/models';
import { PatientDataFacadeService } from '../../../core/services/patient-data-facade.service';

@Component({
  selector: 'app-add-target-data',
  templateUrl: './add-target-data.component.html',
  styleUrl: './add-target-data.component.css',
  standalone: false,
})
export class AddTargetDataComponent implements OnInit {

  @Input({ required: true }) selectedPatientID!: number;
  @Input({ required: false }) patientSession: PatientSessionModel | null = null;
  @Input({ required: false }) target: PatientTargetModel | null = null;
  @Input({ required: false }) hideTargetSelector: boolean = false;
  @Input({ required: false }) hideSessionSelector: boolean = false;
  @Output() close = new EventEmitter<boolean>();

  isLoading = signal(false);

  addTargetForm = new FormGroup({
    recordedDate: new FormControl<string | null>(null, { validators: [Validators.required] }),
    target: new FormControl<PatientTargetModel | null>(null, { validators: [Validators.required] }),
    selectedPromptCode: new FormControl<PromptCodeModel | null>(null),
    selectedEchoicRate: new FormControl<string | null>(null),
    textValue: new FormControl<string | null>(null),
    duration: new FormControl<number | null>(null),
    happened: new FormControl<boolean>(false),
    incorrectCount: new FormControl<number | null>(null),
    correctCount: new FormControl<number | null>(null),
    selectedStaff: new FormControl< SelectItem<DataTakerStaffDataModel> | null>(null),
  });

  titleName: string = 'Add Target Data';
  actionName: string = 'Add';

  targets = signal<SelectItem[]>([]);
  sessions = signal<SelectItem<SessionSimpleDataModel>[]>([]);
  selectedTarget = signal<PatientTargetModel | null>(null);
  selectedSession: SelectItem<SessionSimpleDataModel> | null = null;

  // toileting
  urinationOn = signal<boolean>(false);
  urinationOff = signal<boolean>(false);
  bowelMovementOn = signal<boolean>(false);
  bowelMovementOff = signal<boolean>(false);
  noVoid = signal<boolean>(false);

  user: UserModel | null = null;
  
  staffMembers = signal<DataTakerStaffDataModel[]>([]);
  staffOptions = signal<SelectItem<DataTakerStaffDataModel>[]>([]);

  creatingSession = signal<boolean>(false);
  newSessionStartDate: string | null = null;
  newSessionEndDate: string | null = null;

  constructor(
    private supervisorApiService: SupervisorApiService,
    private staffApiService: StaffApiService,
    private graphApiService: GraphApiService,
    private alertService: AlertService,
    private authService: AuthService,
    private patientDataFacade: PatientDataFacadeService
  ) {

  }

  ngOnInit(): void {

    this.authService.user().subscribe((user) => {
      this.user = user;
    });

    if (this.target) {
      this.selectedTarget.set(this.target);
      this.addTargetForm.controls.target.setValue(this.target);
    }

    if (this.patientSession) {
      this.hideSessionSelector = true;
      this.selectedSession = { value: this.patientSession, label: this.formattedDates(this.patientSession.StartedDate, this.patientSession.FinishedDate) + (this.patientSession.ServiceID ? (' (Service: #' + this.patientSession.ServiceID + ' - ' + (this.patientSession.StaffNames ?? []).join(', ') + ')') : '') };
      this.loadStaffFromSession(this.patientSession);
    }

    if (!this.hideSessionSelector) {
      this.loadSessions();
    }

    this.loadTargets();
  }
  
  loadStaffMembersForSelection() {

    const selectedSessionValue = this.selectedSession?.value;

    // if no session is selected, just return;
    if (!selectedSessionValue) {
      return;
    } 
    
    if (!selectedSessionValue.ServiceID) {
      // if session has no service, just use the current user
      const currentUserStaff: DataTakerStaffDataModel = {
        StaffID: this.user?.ID ?? 0,
        FullName: this.user?.Fullnane ?? '',
        Email: null
      };
      this.staffMembers.set([currentUserStaff]);
      this.updateStaffOptions();

      // Preselect current user
      this.preselectCurrentUser();
    } else if (selectedSessionValue.Staffs && selectedSessionValue.Staffs.length > 0) {
        this.staffMembers.set(selectedSessionValue.Staffs);
        this.updateStaffOptions();
        
        this.preselectFirstStaffFromSession(selectedSessionValue.Staffs);
    }
  }
  
  loadStaffFromSession(session: PatientSessionModel | SessionSimpleDataModel) {
    // When session is selected, reload staff members (includes session staff)
    this.loadStaffMembersForSelection();
  }

  preselectCurrentUser() {
    if (this.user && this.user.ID) {
      const currentUserOption = this.staffOptions().find(s => s.value?.StaffID === this.user!.ID);
      if (currentUserOption) {
        const selectedItemStaff: SelectItem<DataTakerStaffDataModel> = {
          label: currentUserOption.label,
          value: currentUserOption.value
        };
        this.addTargetForm.controls.selectedStaff.setValue(selectedItemStaff);
      }
    }
  }

  preselectFirstStaffFromSession(staff: DataTakerStaffDataModel[]) {
    if (staff && staff.length > 0) {
      const staffOption = this.staffOptions().find(s => s.value?.StaffID === staff[0].StaffID);
      if (staffOption) {
        const selectedItemStaff: SelectItem<DataTakerStaffDataModel> = {
          label: staffOption.label,
          value: staffOption.value
        };
        this.addTargetForm.controls.selectedStaff.setValue(selectedItemStaff);
      }
    }
  }
  
  updateStaffOptions() {
    this.staffOptions.set(
      this.staffMembers().map(staff => ({
        label: staff.FullName ?? '',
        value: staff
      }))
    );
  }
  
  changedTarget(event: any) {
    if (event.value) {
      this.selectedTarget.set(event.value);
      this.loadStaffMembersForSelection();
    }
  }

  loadTargets() {
    this.isLoading.set(true);

    this.patientDataFacade.getTargets(this.selectedPatientID ?? 0, { onlyActive: false }).subscribe({
      next: (targets) => {
        this.handleIncomingTargetsForAddData(targets);
        this.isLoading.set(false);
        const now = new Date();
        const localISOString = new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
        this.addTargetForm.controls.recordedDate.setValue(localISOString);
        this.addTargetForm.controls.recordedDate.updateValueAndValidity();

      },
      error: () => this.isLoading.set(false),
    });
  }

  loadSessions() {
    this.isLoading.set(true);
    this.staffApiService.GetPatientSessionSimpleList({ patientID: this.selectedPatientID ?? 0 }).subscribe({
      next: (sessions) => {
        this.sessions.set(sessions.map((s) => ({ value: s, label: this.formattedDates(s.StartedDate, s.FinishedDate) + (s.ServiceID ? (' (Service: #' + s.ServiceID + ' - ' + (s.Staffs?.map(st => st.FullName ?? '').join(', ') ?? []) + ')') : '') })));
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });
  }

  showCreateSession() {
    this.creatingSession.set(true);
    this.selectedSession = null;
  }

  hideCreateSession() {
    this.creatingSession.set(false);
    this.newSessionStartDate = null;
    this.newSessionEndDate = null;
  }

  formattedDates(startDate: string | null | undefined, endDate: string | null | undefined) {
    if (!startDate || !endDate) {
      return 'Not started';
    }

    //if on the same day, return the date and 2 times
    if (new Date(startDate).toDateString() === new Date(endDate).toDateString()) {
      let result = new Date(startDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
      //add end time to the result
      result += ' - ' + new Date(endDate).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      return result;
    }

    if (!endDate) {
      return new Date(startDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) + ' - not finished';
    }

    //date in fomrat MM/DD/YYYY hh:mm AM/PM
    return new Date(startDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) + ' - ' + new Date(endDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  }


  changedSession(event: any) {
    console.log(event);
    
    // Update selectedSession when session changes
    this.selectedSession = event.value;
    
    // Reload staff members if target is already selected
    // This ensures staff from the selected session are included
    if (this.selectedTarget()) {
      this.loadStaffMembersForSelection();
    }
  }

  handleIncomingTargetsForAddData(targets: PatientTargetModel[]) {
    // filter out targets that are groupings of other targets
    const targetsWithSubtargets = targets.filter((t) => t.SubTargets && t.SubTargets.length > 0);

    let filteredTargets = targets.filter((t) => !targetsWithSubtargets.includes(t));

    // filter out deleted or archived targets
    filteredTargets = filteredTargets.filter((t) => !t.IsDeleted && !t.IsArchived);

    this.targets.set(filteredTargets.map((t) => ({ value: t, label: this.displayNameForTarget(t, targetsWithSubtargets) })));
  }

  displayNameForTarget(target: PatientTargetModel, targetsWithSubtargets: PatientTargetModel[]) {
    if (target.IsTargetSubtarget) {
      const parentTargets = targetsWithSubtargets.filter((t) => t.SubTargets?.some((st) => st.ID === target.ID));
      return target.TargetName + ' (' + Utils.patientTargetTypeLabel(target?.TargetType!) + ')' + ' - (grouped in target(s): ' + parentTargets.map((t) => t.TargetName).join(', ') + ')';
    } else {
      return target.TargetName + ' (' + Utils.patientTargetTypeLabel(target?.TargetType!) + ')';
    }
  }

  intValueForTarget() {
    if (this.targetIsPromptBased()) {
      return this.addTargetForm.controls.selectedPromptCode.value;
    } else if (this.targetIsFrequency()) {
      return 1;
    } else if (this.targetIsDuration() || this.targetIsToileting()) {
      return this.minutesToSeconds(this.addTargetForm.controls.duration.value!);
    } else if (this.targetIsWholePartialInterval()) {
      return this.addTargetForm.controls.happened.value ? 1 : 0;
    } else if (this.targetIsFluencyRate()) {
      return this.addTargetForm.controls.correctCount.value!;
    } else {
      return this.addTargetForm.controls.textValue.value;
    }
  }

  textValueForTarget() {
    if (this.targetIsPromptBased()) {
      const promptCode = this.selectedTarget()?.PromptCodes?.find((p) => p.ID === this.addTargetForm.controls.selectedPromptCode.value);
      if (this.targetIsEchoic()) {
        return promptCode?.Text! + this.addTargetForm.controls.selectedEchoicRate.value;
      } else {
        return promptCode?.Text;
      }
    } else if (this.targetIsIntValueOnly()) {
      return null;
    } else if (this.targetIsFrequency()) {
      return '1';
    } else if (this.targetIsDuration()) {
      const minutes = this.addTargetForm.controls.duration.value!;
      const seconds = this.minutesToSeconds(minutes);
      // format from seconds to hh:mm string
      const minutes1 = Math.floor((seconds % 3600) / 60);
      const seconds1 = seconds % 60;
      return `${minutes1 < 10 ? '0' + minutes1 : minutes1}:${seconds1 < 10 ? '0' + seconds1 : seconds1}`;
    } else if (this.targetIsToileting()) {
      return this.minutesToSeconds(this.addTargetForm.controls.duration.value!).toString();
    } else if (this.targetIsWholePartialInterval()) {
      return this.addTargetForm.controls.happened.value ? '1' : '0';
    } else if (this.targetIsFluencyRate()) {
      return this.addTargetForm.controls.correctCount.value! + '/' + this.addTargetForm.controls.incorrectCount.value!;
    } else {
      return this.addTargetForm.controls.textValue.value;
    }
  }

  targetIsIntValueOnly() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.Quantity;
  }

  onSubmit() {
    console.log(this.addTargetForm.value);

    // validate form
    if (this.addTargetForm.invalid) {
      this.alertService.error('Data is not valid, please check the error messages and try again.');
      return;
    }

    const recordedDateToSend = this.addTargetForm.controls.recordedDate.value ?
      this.addTargetForm.controls.recordedDate.value :
      new Date().toServerString();

    if (!this.patientSession && !this.selectedSession && !this.creatingSession()) {
      this.alertService.error('Session is required');
      return;
    }

    if (this.creatingSession() && (!this.newSessionStartDate || !this.newSessionEndDate)) {
      this.alertService.error('Session dates are required');
      return;
    }

    if ((this.intValueForTarget() === null || this.intValueForTarget() === undefined)  && !this.textValueForTarget() ) {
      this.alertService.error('Target data is not valid, please add some value to the target.');
      return;
    }

    const sessionIDToSend = this.patientSession ? this.patientSession?.ID : this.selectedSession?.value?.ID;

    // Get selected staff or use current user
    const selectedStaffValue = this.addTargetForm.controls.selectedStaff.value;
    const staffID = selectedStaffValue?.value?.StaffID ?? this.user?.ID ?? 0;
    const staffFullName = selectedStaffValue?.value?.FullName ?? this.user?.Fullnane ?? '';
    
    // build target data to be saved
    const targetData: PatientTargetDataModel = {
      PatientSessionID: sessionIDToSend,
      PatientTargetID: this.selectedTarget()?.ID ?? 0,
      RecordedDate: recordedDateToSend,
      IntValue: Number(this.intValueForTarget()) ?? null,
      TextValue: this.textValueForTarget() ?? null,
      UserID: staffID, // Use selected staff from dropdown, or current user
      UserFullName: staffFullName,
    }
    
    // Note: ModifiedByUserID, ModifiedDate, ManuallyAddedByUserID, and ManuallyAddedDate 
    // are all set by the backend and should not be set here

    if (this.targetIsToileting()) {
      const toiletingData: PatientTargetDataToileting = {
        UrinationOn: this.urinationOn(),
        UrinationOff: this.urinationOff(),
        BowelMovementOn: this.bowelMovementOn(),
        BowelMovementOff: this.bowelMovementOff(),
        NoVoid: this.noVoid(),
        Duration: this.minutesToSeconds(this.addTargetForm.controls.duration.value!),
      }
      targetData.ToiletingData = toiletingData;
    }

    console.log(targetData);

    // append new target data to patient session
    this.saveTargetData(targetData);
  }

  saveTargetData(targetData: PatientTargetDataModel) {
    this.isLoading.set(true);
    console.log('Saving target data:', targetData);

    if (this.creatingSession()) {
      this.staffApiService.SavePatientSession({
        body: {
          PatientID: this.selectedPatientID,
          StartedDate: this.newSessionStartDate!,
          FinishedDate: this.newSessionEndDate!,
        },
      }).subscribe(
        {
          next: (response) => {
            console.log('Created session:', response);
            targetData.PatientSessionID = response.ID;
            this.doSaveTargetData(targetData);
          },
          error: (error) => {
            console.error('Error creating session:', error);
            this.alertService.error('Error creating session - ' + error.Message);
            this.isLoading.set(false);
          }
        }
      );
    } else {
      this.doSaveTargetData(targetData);
    }
  }

  doSaveTargetData(targetData: PatientTargetDataModel) {
    this.isLoading.set(true);
    this.staffApiService
      .SavePatientTargetData({
        body: targetData!,
        addedManually: true,
      })
      .subscribe(
        {
          next: (response) => {
            console.log('Saved target data:', response);
            this.alertService.success('Target data saved successfully');
            this.isLoading.set(false);
            this.close.emit(true);
          },
          error: (error) => {
            console.error('Error saving target data:', error);
            this.alertService.error(
              'Error saving target data - ' + error.Message
            );
            this.isLoading.set(false);
          }
        }
      );
  }


  targetIsPromptBased() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return targetIsPromptBased(target);
  }

  targetIsEchoic() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return targetIsEchoic(target);
  }

  targetIsToileting() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.Toileting;
  }

  targetIsFrequency() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.Frequency;
  }

  targetIsDuration() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.Duration;
  }

  targetIsWholePartialInterval() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.WholePartialInterval;
  }

  targetIsFluencyRate() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.FluencyRate;
  }

  promptOptionsForTarget() {
    const target = this.selectedTarget();
    if (!target) {
      return [];
    }
    const promptOptionsForTarget = target?.PromptCodes?.map((p) => {
      return {
        value: p.ID,
        text: p.Text,
      };
    });
    return promptOptionsForTarget;
  }

  minutesToSeconds(minutes: number): number {
    return Math.round(minutes * 60);
  }

  secondsToMinutes(seconds: number): number {
    return seconds / 60;
  }

  extraOptionsForEchoicTarget() {
    const extraOptionsForEchoicTarget = [
      {
        value: '|Articulation',
        text: 'Articulation',
      },
      {
        value: '|Volume',
        text: 'Volume',
      },
      {
        value: '|Speed',
        text: 'Speed',
      },
      {
        value: '|Tone',
        text: 'Tone',
      },
      {
        value: '|Rhythm',
        text: 'Rhythm',
      },
    ];
    return extraOptionsForEchoicTarget;
  }


  onCancel() {
    this.close.emit(true);
  }

  selectedSessionLabel(session: SessionSimpleDataModel) {
    if (session) {
      return 'Session: ' + this.formattedDates(session.StartedDate, session.FinishedDate) + (session.ServiceID ? (' (Service: #' + session.ServiceID + ' - ' + (session.Staffs?.map(st => st.FullName ?? '').join(', ') ?? []) + ')') : '');
    } else {
      return '';
    }
  }

  shouldShowStaffSelector() {
    return !this.hideSessionSelector && this.selectedSession !== null && this.selectedSession !== undefined && !this.creatingSession();
  }
}
